/**
 * Display helpers.
 *
 * The rule these encode: a number never appears without its data class. Money
 * is stored in minor units and formatted in locale form, but the exact minor
 * value is what travels over the wire (FR-005, PRD 10.3).
 */

import type { DataClass, FeatureValue, Freshness } from './types'

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
})

export function money(minor: number | null | undefined, currency = 'INR'): string {
  if (minor === null || minor === undefined) return '—'
  const major = minor / 100
  if (currency === 'INR') return inr.format(major)
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(major)
}

/** Indian lakh/crore shorthand, which is how planners read budgets. */
export function moneyShort(minor: number | null | undefined, currency = 'INR'): string {
  if (minor === null || minor === undefined) return '—'
  const major = minor / 100
  const symbol = currency === 'INR' ? '₹' : `${currency} `
  if (Math.abs(major) >= 1e7) return `${symbol}${(major / 1e7).toFixed(2)} Cr`
  if (Math.abs(major) >= 1e5) return `${symbol}${(major / 1e5).toFixed(2)} L`
  return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(major)}`
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function dateRange(start: string | null, end: string | null): string {
  if (!start && !end) return 'Dates not set'
  if (start && end) {
    const days =
      Math.round(
        (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000,
      ) + 1
    return `${shortDate(start)} – ${shortDate(end)} (${days} days)`
  }
  return shortDate(start ?? end)
}

/** Human label for a data class, used on every metric chip. */
export const DATA_CLASS_LABEL: Record<DataClass, string> = {
  measured: 'Measured',
  modelled: 'Modelled',
  estimated: 'Estimated',
  synthetic: 'Sample data',
  unavailable: 'Unavailable',
}

export const FRESHNESS_LABEL: Record<Freshness, string> = {
  fresh: 'Fresh',
  aging: 'Aging',
  stale: 'Stale',
  missing: 'No data',
}

/** Format a feature's value with its unit, without asserting availability. */
export function featureValue(fv: FeatureValue | null | undefined): string {
  if (!fv || fv.value === null || fv.data_class === 'unavailable') return 'Unavailable'
  switch (fv.unit) {
    case 'index_0_1':
    case 'share_0_1':
      return percent(fv.value, fv.value < 0.1 ? 1 : 0)
    case 'minor_units_per_1000_target_impressions':
      return money(fv.value)
    case 'm':
      return fv.value >= 1000 ? `${(fv.value / 1000).toFixed(1)} km` : `${Math.round(fv.value)} m`
    case 'kmph':
      return `${fv.value.toFixed(0)} km/h`
    case 's':
      return fv.value >= 60 ? `${(fv.value / 60).toFixed(1)} min` : `${fv.value.toFixed(0)} s`
    case 'minutes':
      return `${fv.value.toFixed(1)} min`
    case 'ratio':
      return fv.value.toFixed(2)
    case 'exposures_per_unique_passer':
      return `${fv.value.toFixed(1)}×`
    case 'people':
      return count(fv.value)
    default:
      return count(fv.value)
  }
}

/** A one-line provenance string suitable for a tooltip. */
export function provenanceLine(fv: FeatureValue | null | undefined): string {
  if (!fv) return 'No data'
  if (fv.data_class === 'unavailable') return fv.unavailable_reason ?? 'No data available'
  const parts: string[] = [DATA_CLASS_LABEL[fv.data_class]]
  if (fv.provenance.provider_name) parts.push(fv.provenance.provider_name)
  else if (fv.provenance.provider_id) parts.push(fv.provenance.provider_id)
  parts.push(FRESHNESS_LABEL[fv.freshness].toLowerCase())
  if (fv.confidence !== null) parts.push(`confidence ${percent(fv.confidence)}`)
  if (fv.provenance.coverage !== null && fv.provenance.coverage !== undefined) {
    parts.push(`coverage ${percent(fv.provenance.coverage)}`)
  }
  if (fv.provenance.methodology_version) parts.push(`method ${fv.provenance.methodology_version}`)
  const line = parts.join(' · ')
  return fv.unavailable_reason ? `${line}\n${fv.unavailable_reason}` : line
}

export const OBJECTIVE_LABEL: Record<string, string> = {
  new_launch: 'New launch',
  awareness: 'Awareness',
  consideration: 'Consideration',
  footfall: 'Footfall',
  retention: 'Retention',
  tactical: 'Tactical',
  unspecified: 'Not specified',
}

export const AFFLUENCE_LABEL: Record<string, string> = {
  mass: 'Mass',
  mid: 'Mid market',
  mid_to_premium: 'Mid to premium',
  premium: 'Premium',
  unspecified: 'Not specified',
}

export const ASSET_CLASS_LABEL: Record<string, string> = {
  static: 'Static',
  transit: 'Transit',
  dooh: 'DOOH',
}

export const OBJECTIVE_FUNCTION_LABEL: Record<string, string> = {
  balanced: 'Balanced',
  max_target_impressions: 'Max target impressions',
  max_eots: 'Max effective OTS',
  max_suitability: 'Max suitability',
  min_cpti: 'Lowest CPTI',
}

export const EXCLUSION_LABEL: Record<string, string> = {
  no_price: 'No usable price',
  unavailable_window: 'Occupied in the window',
  over_single_asset_budget: 'Over the per-asset budget cap',
  outside_geography: 'Outside the requested area',
  media_type_excluded: 'Media type not requested',
  inactive: 'Inactive in Captura',
  no_coordinates: 'No coordinates',
  lease_expired: 'Lease does not cover the window',
  explicitly_excluded: 'Excluded by the planner',
  policy_blocked: 'Blocked by policy',
  govt_client_restricted: 'Government inventory',
  not_illuminated: 'Not illuminated',
  removed: 'Removed from inventory',
  test_asset: 'Test asset',
  strategy_tier_excluded: 'Tier not requested',
  asset_class_excluded: 'Asset class not requested',
}

export function titleCase(value: string): string {
  return value
    .replace(/[_.]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export type MessageBlock =
  | { type: 'text'; text: string }
  | { type: 'table'; header: string[]; rows: string[][] }

function splitTableRow(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|')) s = s.slice(0, -1)
  return s.split('|').map((cell) => cell.trim())
}

//: e.g. "|---|:--:|---:|" - the row markdown uses to separate a table's
//: header from its body. Never appears in prose, so it is all this needs to
//: detect a table.
const TABLE_SEPARATOR_ROW = /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/

/**
 * The assistant sometimes answers in a markdown table (e.g. "list the
 * eligible assets"). Split its text into plain-text and table segments so
 * the chat can render an actual table instead of raw "| a | b |" text.
 */
export function parseMessageBlocks(text: string): MessageBlock[] {
  const lines = text.split('\n')
  const blocks: MessageBlock[] = []
  let buffer: string[] = []

  const flushText = () => {
    const joined = buffer.join('\n').trim()
    if (joined) blocks.push({ type: 'text', text: joined })
    buffer = []
  }

  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const next = lines[i + 1]
    if (line.includes('|') && next !== undefined && TABLE_SEPARATOR_ROW.test(next.trim())) {
      flushText()
      const header = splitTableRow(line)
      const rows: string[][] = []
      i += 2
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(splitTableRow(lines[i]))
        i += 1
      }
      blocks.push({ type: 'table', header, rows })
      continue
    }
    buffer.push(line)
    i += 1
  }
  flushText()
  return blocks
}
